import type { EventType, EventStatus, EventModality, EventCategory } from '@/mocks/adminEvents';
import {
  eventTypeLabels, eventTypeIcons, eventTypeColors,
  eventStatusLabels,
  eventModalityLabels, eventModalityIcons, eventModalityColors,
  eventCategoryLabels, eventCategoryIcons, eventCategoryColors,
} from '@/mocks/adminEvents';

interface EventFiltersProps {
  search: string;
  onSearchChange: (val: string) => void;
  selectedType: EventType | 'todos';
  onTypeChange: (t: EventType | 'todos') => void;
  selectedStatus: EventStatus | 'todos';
  onStatusChange: (s: EventStatus | 'todos') => void;
  selectedModality: EventModality | 'todos';
  onModalityChange: (m: EventModality | 'todos') => void;
  selectedCategory: EventCategory | 'todos';
  onCategoryChange: (c: EventCategory | 'todos') => void;
  counts: {
    todos: number;
    types: Record<string, number>;
    statuses: Record<string, number>;
    modalities: Record<string, number>;
    categories: Record<string, number>;
  };
}

const types: (EventType | 'todos')[] = ['todos', 'concierto', 'meet_greet', 'transmision_especial', 'firma_autografos', 'sesion_acustica', 'lanzamiento'];
const statuses: (EventStatus | 'todos')[] = ['todos', 'publicado', 'borrador', 'finalizado', 'cancelado'];
const modalities: (EventModality | 'todos')[] = ['todos', 'presencial', 'online', 'hibrido'];
const categories: (EventCategory | 'todos')[] = ['todos', 'musica', 'deportes', 'comunidad', 'cultural', 'religioso', 'familiar', 'educativo'];

export default function EventFilters({
  search, onSearchChange,
  selectedType, onTypeChange,
  selectedStatus, onStatusChange,
  selectedModality, onModalityChange,
  selectedCategory, onCategoryChange,
  counts,
}: EventFiltersProps) {
  const modalityCount = counts.modalities.presencial + counts.modalities.online + counts.modalities.hibrido || 0;

  return (
    <div className="space-y-3">
      {/* Search + Status */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative w-full sm:max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar evento..."
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors"
          />
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => onStatusChange(s)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                selectedStatus === s
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
              }`}
            >
              {s === 'todos' ? 'Todos' : eventStatusLabels[s]}
              {counts.statuses[s] !== undefined && (
                <span className={`text-[9px] ${selectedStatus === s ? 'text-white/70' : 'text-foreground-400'}`}>
                  ({counts.statuses[s]})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Modality filters — MAIN FOCUS */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {modalities.map((m) => {
          if (m === 'todos') {
            return (
              <button
                key="mod-todos"
                onClick={() => onModalityChange('todos')}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  selectedModality === 'todos'
                    ? 'bg-primary-500 text-background-50 shadow-sm'
                    : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
                }`}
              >
                <i className="ri-stack-line text-[11px]"></i>
                Todas las modalidades
                <span className={`text-[9px] ${selectedModality === 'todos' ? 'text-white/70' : 'text-foreground-400'}`}>
                  ({modalityCount})
                </span>
              </button>
            );
          }
          const mc = eventModalityColors[m];
          const isActive = selectedModality === m;
          return (
            <button
              key={m}
              onClick={() => onModalityChange(m)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap border ${
                isActive
                  ? mc.bg + ' ' + mc.text + ' ' + mc.border + ' ring-2 ring-offset-1 ring-primary-300 shadow-sm'
                  : mc.bg + ' ' + mc.text + ' ' + mc.border + ' opacity-70 hover:opacity-100'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${mc.dot}`}></span>
              {eventModalityLabels[m]}
              <span className="opacity-60">({counts.modalities[m] || 0})</span>
            </button>
          );
        })}
      </div>

      {/* Category filters */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {categories.map((c) => {
          if (c === 'todos') {
            return (
              <button
                key="cat-todos"
                onClick={() => onCategoryChange('todos')}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  selectedCategory === 'todos'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
                }`}
              >
                <i className="ri-price-tag-3-line text-[11px]"></i>
                Categoría ({counts.todos})
              </button>
            );
          }
          const cc = eventCategoryColors[c];
          return (
            <button
              key={c}
              onClick={() => onCategoryChange(c)}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold cursor-pointer transition-all whitespace-nowrap border ${
                selectedCategory === c
                  ? cc.bg + ' ' + cc.text + ' ' + cc.border + ' ring-1 ring-primary-300'
                  : cc.bg + ' ' + cc.text + ' ' + cc.border + ' opacity-65 hover:opacity-100'
              }`}
            >
              <i className={`${eventCategoryIcons[c]} text-[10px]`}></i>
              {eventCategoryLabels[c]}
              <span className="opacity-60">({counts.categories[c] || 0})</span>
            </button>
          );
        })}
      </div>

      {/* Type filters — collapsed by default, secondary */}
      <details className="group">
        <summary className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-50 border border-background-200 text-foreground-500 text-xs font-semibold cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap select-none">
          <i className="ri-apps-line text-[11px]"></i>
          Tipo de evento
          <i className="ri-arrow-down-s-line text-[10px] group-open:rotate-180 transition-transform"></i>
        </summary>
        <div className="flex items-center gap-1.5 flex-wrap mt-2 pl-0.5">
          {types.map((t) => {
            if (t === 'todos') return null;
            const colors = eventTypeColors[t];
            return (
              <button
                key={t}
                onClick={() => onTypeChange(t)}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold cursor-pointer transition-all whitespace-nowrap border ${
                  selectedType === t
                    ? colors.bg + ' ' + colors.text + ' ' + colors.border + ' ring-1 ring-primary-300'
                    : colors.bg + ' ' + colors.text + ' ' + colors.border + ' opacity-65 hover:opacity-100'
                }`}
              >
                <i className={`${eventTypeIcons[t]} text-[10px]`}></i>
                {eventTypeLabels[t]}
                {counts.types[t] !== undefined && (
                  <span className="opacity-60">({counts.types[t]})</span>
                )}
              </button>
            );
          })}
        </div>
      </details>
    </div>
  );
}